import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

import { useApiAuthPermissoesUsuario } from "@hooks/api/auth";
import { ApiInterceptorError } from "@classes/ApiInterceptorError";

import useAuth from "@hooks/useAuth";
import { useCorban } from "@hooks/useCorban";
import { PRODUCT_TYPE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";
import { User } from "@contexts/Auth";

type PermissionsContext = {
  permissions: Record<string, string>;
  routes: Record<string, boolean>;
  allowedRoutes: string[];
  homeRoute: string;
  isReady: boolean;
  hasPermission: (permission: string) => boolean;
  acceptedTerms: boolean;
  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsContext>({
  permissions: {},
  routes: {},
  allowedRoutes: [],
  homeRoute: "",
  isReady: false,
  hasPermission: () => false,
  acceptedTerms: false,
  loading: true,
});

export const PermissionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    fetchPermissoesUsuario,
    {
      data: fetchPermissionsData,
      loading: permissionsLoading,
      reset: resetFetchPermissions,
    },
  ] = useApiAuthPermissoesUsuario();

  const {
    user,
    updateUser,
    setProducts,
    hasProduct,
    loading: userLoading,
    onExpireSession,
  } = useAuth();
  const { isReady: isReadyCorban } = useCorban();

  const [permissions, setPermissions] = useState<
    PermissionsContext["permissions"]
  >({});
  const [loading, setLoading] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const clearData = useCallback(() => {
    setPermissions({});
    resetFetchPermissions();
  }, [resetFetchPermissions]);

  const hasPermission = useCallback(
    (permission: string) => !!permissions[permission],
    [permissions],
  );

  const routes: Record<string, boolean> = useMemo(() => {
    return {
      "/servicos": hasPermission("add_contrato"),
      "/inss/aumento-margem":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE_AUMENTO) &&
        hasPermission("add_contrato"),
      "/inss/aumento-margem-lote":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE_AUMENTO) &&
        hasPermission("add_contrato"),
      "/inss/margem-livre":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE) && hasPermission("add_contrato"),
      "/inss/portabilidade":
        hasProduct(PRODUCT_TYPE.PORTABILIDADE) && hasPermission("add_contrato"),
      "/inss/portabilidade-refin":
        hasProduct(PRODUCT_TYPE.PORTABILIDADE_REFIN) &&
        hasPermission("add_contrato"),
      "/inss/refinanciamento":
        hasProduct(PRODUCT_TYPE.REFINANCIAMENTO) &&
        hasPermission("add_contrato"),
      "/receba":
        hasProduct(PRODUCT_TYPE.RECEBA) && hasPermission("add_contrato"),
      "/consignado-privado": hasProduct(PRODUCT_TYPE.CONSIGNADO_PRIVADO),
      "/cartao/cartao-beneficio":
        hasProduct(PRODUCT_TYPE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/cartao/agregacao-margem":
        hasProduct(PRODUCT_TYPE.AGREGACAO_MARGEM) &&
        hasPermission("add_contrato"),
      "/saque-complementar":
        hasProduct(PRODUCT_TYPE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/emprestimo-privado": hasProduct(PRODUCT_TYPE.CONSIGNADO_PRIVADO),
      "/emprestimo-privado/detalhes": hasProduct(
        PRODUCT_TYPE.CONSIGNADO_PRIVADO,
      ),
      "/contratos": true,
      "/usuarios": hasPermission("happy_can_create_user"),
      "/usuarios/novo": hasPermission("happy_can_create_user"),
      "/selecao-corban": true,
      "/termos-de-uso": true,
      "/politica-de-privacidade": true,
      // TODO: Feature flag refinanciamento
      "/portabilidade-ataque": hasPermission("can_acess_portability_attacks"),

      "/sem-acesso": true,

      // Submenu keys
      "/inss": [
        PRODUCT_TYPE.MARGEM_LIVRE,
        PRODUCT_TYPE.PORTABILIDADE,
        PRODUCT_TYPE.PORTABILIDADE_REFIN,
        PRODUCT_TYPE.REFINANCIAMENTO,
      ].some(hasProduct),

      "/cartao": [
        PRODUCT_TYPE.CARTAO_BENEFICIO,
        PRODUCT_TYPE.AGREGACAO_MARGEM,
      ].some(hasProduct),
    };
  }, [hasProduct, hasPermission]);

  const allowedRoutes = useMemo(() => {
    return Object.keys(routes).filter((route) => routes[route]);
  }, [routes]);

  const homeRoute = useMemo(() => allowedRoutes[0] || "", [allowedRoutes]);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await fetchPermissoesUsuario();

      const userData: Partial<User> = {
        name: data.name,
        uniqueId: data.unique_id,
        identifier: data.identifier,
        enableFaceMatch: data.enable_face_match,
        isChecked: data.is_checked,
        checkUrl: data.check_url,
        supportChat: data.possui_chat_suporte,
        allowIlliterate: !!data.corban?.permite_analfabeto,
        allowLegalRepresentative: !!data.corban?.permite_representante,
        allowCardLegalRepresentative:
          !!data.corban?.permite_representante_cartao,
        id: data.id,
      };

      updateUser(userData);

      setPermissions(
        data.permissions.reduce(
          (permissionsHashList, permission) => ({
            ...permissionsHashList,
            [permission]: true,
          }),
          {},
        ),
      );

      setProducts(
        data.produtos.map((product) => ({
          nome: product.nome,
          tipo_produto: getProductTypeFromProductCode(product.cd_produto),
        })),
      );

      setAcceptedTerms(data.termo_uso_privacidade);
    } catch (e) {
      if (
        !(e instanceof ApiInterceptorError) ||
        !axios.isCancel(e.sourceError)
      ) {
        clearData();
        console.error("[fetchPermissions] error", { error: e });
        onExpireSession("");
      }
    } finally {
      setLoading(false);
    }
  }, [
    fetchPermissoesUsuario,
    onExpireSession,
    updateUser,
    setProducts,
    clearData,
  ]);

  useEffect(() => {
    if (!userLoading) {
      if (user?.refreshToken) {
        if (isReadyCorban) void fetchPermissions();
      } else {
        clearData();
        setLoading(false);
      }
    }
  }, [
    user?.refreshToken,
    userLoading,
    fetchPermissions,
    clearData,
    isReadyCorban,
  ]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        routes,
        allowedRoutes,
        homeRoute,
        isReady: !!fetchPermissionsData,
        hasPermission,
        acceptedTerms,
        loading: loading || permissionsLoading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
